import React, { useRef, useState } from 'react';
import { Camera, Loader2, X } from 'lucide-react';
import { apiFetch } from '@/lib/supabaseClient';
import { createAttendanceRequestId, scanAttendanceQr } from '@/lib/attendanceQr';

export default function AttendanceQrCheckin({ onClose, onCompleted }) {
  const [busy, setBusy] = useState(false);
  const [notice, setNotice] = useState('');
  const [result, setResult] = useState(null);
  const [error, setError] = useState('');
  const [pending, setPending] = useState(null);
  const scanInFlight = useRef(false);
  const submitInFlight = useRef(false);

  const submit = async (scan) => {
    if (!scan || submitInFlight.current) return;
    submitInFlight.current = true;
    setBusy(true);
    setError('');
    setNotice('');
    try {
      const result = await apiFetch('/api/attendance/qr/commit', {
        method: 'POST',
        body: JSON.stringify({
          token: scan.token,
          request_id: scan.requestId,
        }),
      });
      const message = result.idempotent
        ? 'Questa scansione era già stata registrata: nessuna seconda timbratura.'
        : result.outcome === 'DA_VALIDARE'
          ? 'Timbratura acquisita e inviata per verifica.'
          : result.event_type === 'ENTRY'
            ? 'Entrata registrata.'
            : result.event_type === 'EXIT'
              ? 'Uscita registrata.'
              : 'Evento acquisito e inviato per verifica.';
      setPending(null);
      setNotice(message);
      setResult(result);
      await onCompleted?.();
    } catch (requestError) {
      if (
        requestError?.status === 409
        && requestError?.message === 'QR scaduto. In attesa del rinnovo.'
      ) {
        setPending(null);
        setError('QR scaduto. In attesa del rinnovo.');
        return;
      }
      setPending(scan);
      setError(
        requestError.status === 401
          ? 'Sessione scaduta. Accedi di nuovo.'
          : requestError.message || 'Timbratura non confermata. Puoi ritentare l’invio senza una nuova scansione.'
      );
    } finally {
      submitInFlight.current = false;
      setBusy(false);
    }
  };

  const startScan = async () => {
    if (scanInFlight.current || submitInFlight.current || pending) return;
    scanInFlight.current = true;
    setBusy(true);
    setError('');
    setNotice('');
    setResult(null);
    try {
      const token = await scanAttendanceQr();
      const scan = { token, requestId: createAttendanceRequestId() };
      setPending(scan);
      scanInFlight.current = false;
      await submit(scan);
    } catch (scanError) {
      if (scanError?.code === 'QR_SCAN_CANCELLED') return;
      setError(scanError.message || 'Scanner QR non disponibile.');
    } finally {
      scanInFlight.current = false;
      setBusy(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[80] flex items-end justify-center bg-slate-950/60 p-3 sm:items-center" data-testid="attendance-qr-dialog-backdrop">
      <section
        role="dialog"
        aria-modal="true"
        aria-labelledby="attendance-qr-dialog-title"
        className="w-full max-w-md rounded-3xl bg-white p-5 pb-[calc(1.25rem+env(safe-area-inset-bottom))] shadow-2xl"
        data-testid="attendance-qr-dialog"
      >
        <div className="flex items-start justify-between gap-3">
          <div>
            <p className="text-xs font-semibold uppercase tracking-wider text-brand-green">Presenze online</p>
            <h2 id="attendance-qr-dialog-title" className="mt-1 font-display text-xl font-semibold text-brand-navy">
              Timbratura QR
            </h2>
          </div>
          <button
            type="button"
            onClick={onClose}
            disabled={busy}
            className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl border border-stone-200 text-stone-600 disabled:opacity-50"
            aria-label="Chiudi"
            data-testid="attendance-qr-close"
          >
            <X className="h-5 w-5" aria-hidden="true" />
          </button>
        </div>
        <p className="mt-3 text-sm leading-relaxed text-stone-600">
          Scansiona il codice sul display del locale. Entrata o uscita e orario sono determinati dal server; serve una connessione internet.
        </p>
        {notice && (
          <div className="mt-4 rounded-xl border border-emerald-200 bg-emerald-50 px-4 py-3 text-sm text-emerald-900" role="status" data-testid="attendance-qr-result">
            <p className="font-semibold">{notice}</p>
            {result?.occurred_at && (
              <p className="mt-1 text-xs font-medium">
                {result.event_type === 'ENTRY' ? 'Entrata' : result.event_type === 'EXIT' ? 'Uscita' : 'Evento'} ·{' '}
                {new Date(result.occurred_at).toLocaleString('it-IT', { timeZone: 'Europe/Rome' })} (ora server)
              </p>
            )}
          </div>
        )}
        {error && <p className="mt-4 rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-900" role="alert" data-testid="attendance-qr-error">{error}</p>}

        {pending ? (
          <button
            type="button"
            onClick={() => submit(pending)}
            disabled={busy}
            className="mt-5 flex min-h-12 w-full items-center justify-center gap-2 rounded-xl bg-brand-green px-4 text-sm font-semibold text-white disabled:opacity-60"
            data-testid="attendance-qr-retry"
          >
            {busy && <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />}
            Riprova l’invio senza scansionare
          </button>
        ) : (
          <button
            type="button"
            onClick={startScan}
            disabled={busy || Boolean(notice)}
            className="mt-5 flex min-h-12 w-full items-center justify-center gap-2 rounded-xl bg-brand-green px-4 text-sm font-semibold text-white disabled:opacity-60"
            data-testid="attendance-qr-scan"
          >
            {busy ? <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" /> : <Camera className="h-4 w-4" aria-hidden="true" />}
            {busy ? 'Elaborazione…' : 'Apri scanner QR'}
          </button>
        )}
        {!pending && notice && (
          <button type="button" onClick={onClose} className="mt-3 min-h-11 w-full rounded-xl border border-stone-200 px-4 text-sm font-semibold text-stone-700">
            Torna alla dashboard
          </button>
        )}
        <p className="mt-4 text-center text-xs text-stone-500">Il codice è monouso. Non fare una nuova scansione se l’invio è in attesa di conferma.</p>
      </section>
    </div>
  );
}
